/**
 * User-facing subscription / plan management page.
 * Shows current plan, INR pricing, detailed feature comparison, and upgrade CTAs.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlan } from '../contexts/PlanContext';
import {
  PLAN_IDS,
  PLANS,
  getPlanById,
  planIncludes,
  FEATURE_LABELS,
  FEATURE_DESCRIPTIONS,
  FEATURE_COMPARISON_ORDER,
  FEATURE_REQUIRED_PLAN,
} from '../constants/plans';
import './Subscription.css';

const PLAN_ORDER = [PLAN_IDS.BASIC, PLAN_IDS.HOST, PLAN_IDS.AD_FREE, PLAN_IDS.PREMIUM];

function formatInr(amount) {
  if (amount === 0) return 'Free';
  return `₹${amount.toLocaleString('en-IN')}`;
}

export default function Subscription() {
  const navigate = useNavigate();
  const { planId, subscriptionStatus } = usePlan();
  const currentPlan = getPlanById(planId);
  const [comparisonExpanded, setComparisonExpanded] = useState(true);
  const [billingInterval, setBillingInterval] = useState('yearly'); // 'monthly' | 'yearly'

  function getDisplayPrice(plan) {
    if (plan.priceInrAnnual === 0 && (plan.priceInrMonthly === 0 || plan.priceInrMonthly == null))
      return { amount: 0, period: null };
    if (plan.yearlyOnly || billingInterval === 'yearly')
      return { amount: plan.priceInrAnnual, period: 'year' };
    return { amount: plan.priceInrMonthly, period: 'month' };
  }

  return (
    <div className="subscription-page">
      <header className="subscription-header">
        <h1 className="subscription-title">Plans & pricing</h1>
        <p className="subscription-subtitle">
          Choose the plan that fits you. All prices in INR. Contact an administrator to change your plan.
        </p>
      </header>

      <section className="subscription-billing-toggle card-ui">
        <span className="subscription-billing-label">Billing</span>
        <div className="subscription-billing-switch" role="group" aria-label="Billing interval">
          <button
            type="button"
            className={`subscription-billing-option ${billingInterval === 'monthly' ? 'subscription-billing-option-active' : ''}`}
            onClick={() => setBillingInterval('monthly')}
            aria-pressed={billingInterval === 'monthly'}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`subscription-billing-option ${billingInterval === 'yearly' ? 'subscription-billing-option-active' : ''}`}
            onClick={() => setBillingInterval('yearly')}
            aria-pressed={billingInterval === 'yearly'}
          >
            Yearly
          </button>
        </div>
        <p className="subscription-billing-note">
          Host and Ad-free are yearly only. Premium can be billed monthly or yearly (yearly saves more).
        </p>
      </section>

      <section className="subscription-current card-ui">
        <h2>Your current plan</h2>
        <div className="subscription-current-badge">
          <span className="subscription-plan-name">{currentPlan.name}</span>
          {currentPlan.tagline && (
            <span className="subscription-plan-tagline">{currentPlan.tagline}</span>
          )}
          <span className="subscription-plan-desc">{currentPlan.description}</span>
          <div className="subscription-current-price">
            {currentPlan.priceInrAnnual === 0 && (currentPlan.priceInrMonthly === 0 || currentPlan.priceInrMonthly == null) ? (
              <span className="subscription-price-main">{formatInr(0)}</span>
            ) : currentPlan.yearlyOnly ? (
              <span className="subscription-price-main">
                {formatInr(currentPlan.priceInrAnnual)}
                <span className="subscription-price-period">/year</span>
              </span>
            ) : (
              <>
                <span className="subscription-price-main">
                  {formatInr(currentPlan.priceInrMonthly)}
                  <span className="subscription-price-period">/month</span>
                </span>
                <span className="subscription-price-annual">
                  or {formatInr(currentPlan.priceInrAnnual)}/year
                </span>
              </>
            )}
          </div>
        </div>
        {subscriptionStatus && (
          <p className="subscription-status">Status: {subscriptionStatus}</p>
        )}
      </section>

      <section className="subscription-pricing">
        <h2>Plan rates (INR)</h2>
        <p className="subscription-pricing-intro">
          Max plan ₹500/year. Lower tiers (Host, Ad-free) are yearly only.
        </p>
        <div className="subscription-plans-grid">
          {PLANS.map((p) => {
            const isCurrent = p.id === currentPlan.id;
            const isUpgrade = PLAN_ORDER.indexOf(p.id) > PLAN_ORDER.indexOf(currentPlan.id);
            const display = getDisplayPrice(p);
            return (
              <div
                key={p.id}
                className={`subscription-plan-card ${isCurrent ? 'subscription-plan-current' : ''}`}
              >
                <div className="subscription-plan-card-header">
                  <span className="subscription-plan-card-name">{p.name}</span>
                  {p.tagline && (
                    <span className="subscription-plan-card-tagline">{p.tagline}</span>
                  )}
                  {isCurrent && <span className="subscription-plan-badge">Current</span>}
                </div>
                <div className="subscription-plan-card-price">
                  {display.period === null ? (
                    <span className="subscription-plan-price-main">{formatInr(0)}</span>
                  ) : p.yearlyOnly ? (
                    <>
                      <span className="subscription-plan-price-main">
                        {formatInr(p.priceInrAnnual)}
                        <span className="subscription-plan-price-period">/year</span>
                      </span>
                      <span className="subscription-plan-price-year-only">Yearly only</span>
                    </>
                  ) : (
                    <>
                      <span className="subscription-plan-price-main">
                        {formatInr(display.amount)}
                        <span className="subscription-plan-price-period">
                          /{display.period === 'year' ? 'year' : 'mo'}
                        </span>
                      </span>
                      {display.period === 'month' && p.priceInrAnnual > 0 && (
                        <span className="subscription-plan-price-year">
                          or {formatInr(p.priceInrAnnual)}/year
                        </span>
                      )}
                    </>
                  )}
                </div>
                <p className="subscription-plan-card-desc">{p.description}</p>
                {isCurrent && (
                  <button type="button" className="btn-secondary subscription-plan-cta" disabled>
                    Current plan
                  </button>
                )}
                {!isCurrent && !isUpgrade && (
                  <button type="button" className="btn-secondary subscription-plan-cta" disabled>
                    Downgrade (contact admin)
                  </button>
                )}
                {!isCurrent && isUpgrade && (
                  <button
                    type="button"
                    className="btn-primary subscription-plan-cta"
                    onClick={() => {}}
                  >
                    Upgrade (contact admin)
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="subscription-comparison card-ui">
        <button
          type="button"
          className="subscription-comparison-toggle"
          onClick={() => setComparisonExpanded((e) => !e)}
          aria-expanded={comparisonExpanded}
        >
          <h2>Feature comparison</h2>
          <span className="subscription-comparison-chevron" aria-hidden>
            {comparisonExpanded ? '▼' : '▶'}
          </span>
        </button>
        <p className="subscription-comparison-intro">
          Compare what each plan includes so you can decide when to upgrade.
        </p>
        {comparisonExpanded && (
          <div className="subscription-comparison-wrap">
            <table className="subscription-comparison-table" role="grid">
              <thead>
                <tr>
                  <th scope="col" className="subscription-comparison-feature-col">
                    Feature
                  </th>
                  {PLANS.map((p) => (
                    <th key={p.id} scope="col" className="subscription-comparison-plan-col">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_COMPARISON_ORDER.map((featureKey) => {
                  const label = FEATURE_LABELS[featureKey];
                  const desc = FEATURE_DESCRIPTIONS[featureKey];
                  const requiredPlan = FEATURE_REQUIRED_PLAN[featureKey];
                  return (
                    <tr key={featureKey}>
                      <td className="subscription-comparison-feature-cell">
                        <span className="subscription-comparison-feature-name">{label}</span>
                        <span className="subscription-comparison-feature-desc">{desc}</span>
                      </td>
                      {PLANS.map((p) => {
                        const included = planIncludes(p.id, requiredPlan);
                        return (
                          <td key={p.id} className="subscription-comparison-plan-cell">
                            {included ? (
                              <span className="subscription-comparison-check" aria-label="Included">
                                ✓
                              </span>
                            ) : (
                              <span className="subscription-comparison-dash" aria-label="Not included">
                                —
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="subscription-footer">
        To change your plan, contact an administrator or use the Admin Panel if you have admin access.
      </p>

      <div className="subscription-actions">
        <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
        <button type="button" className="btn-primary" onClick={() => navigate('/dashboard')}>
          Dashboard
        </button>
      </div>
    </div>
  );
}
